/**
 * In-flight request de-duplication.
 *
 * Collapses concurrent identical GETs into a single network call without caching the
 * response. This is deliberately NOT the axios cache: `axios-cache-interceptor` holds
 * responses for a TTL, and the only invalidation this app has is the 2-second bypass
 * window in axiosConfig.js, which stops a GET reading the cache but never evicts the
 * entry already stored. That combination makes staff hard-refresh to see newly added
 * or updated records. Sharing the pending promise avoids the duplicate request while
 * keeping every fresh mount, navigation, and refresh a real round trip.
 *
 * Entries are removed as soon as the request settles, so nothing survives past it.
 */

const inFlight = new Map();

/**
 * Shallow copy so one caller sorting or splicing the result cannot corrupt another's.
 * One level deep only — nested values are shared and must be treated as read-only.
 */
const shallowCopy = (value) => {
  if (Array.isArray(value)) return [...value];
  if (value && typeof value === 'object') return { ...value };
  return value;
};

/**
 * @param {string} key — identifies the request; callers issuing the same HTTP request
 *   must use the same key (e.g. `users:8`) for the de-duplication to apply.
 * @param {() => Promise<any>} request — invoked only when nothing is already in flight.
 * @returns {Promise<any>} the shared result, shallow-copied per caller.
 */
export const dedupeInFlight = (key, request) => {
  const pending = inFlight.get(key);
  if (pending) {
    return pending.then(shallowCopy);
  }

  const promise = Promise.resolve()
    .then(request)
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise.then(shallowCopy);
};

export default dedupeInFlight;
